import ReactApexChart from 'react-apexcharts';
import UppercaseFirstLetter from '../costing/cosmetic_functions';

const isCountMode = (name) =>
    name === 'Dashboard (Count)' || name === 'Question (Count)';

/** ApexCharts can throw in PathArray.morph when categories and series length differ or when animating mount/unmount; keep numeric data aligned. */
function alignTotalsWithCategories(totalCost, categoryCount) {
    const raw = Array.isArray(totalCost) ? totalCost : [];
    const nums = raw.map((v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    });
    while (nums.length < categoryCount) {
        nums.push(0);
    }
    if (nums.length > categoryCount) {
        nums.length = categoryCount;
    }
    return nums;
}

export default function OvertimeDescription({
    data,
    groupBy,
    groupByName,
    periodicity,
    totalCost,
    /** When true (org-wise / unit costing): vertical column chart of totals; optional stacked breakdown by group. */
    alwaysColumnTotal = false,
    showServiceBreakdown = false,
    /** When true, allow stacked breakdown for daily periodicity (org-wise, unit costing). */
    enableBreakdownForDaily = false,
    /** When true (default), do not stack-breakdown when groupBy is cluster. Set false for unit costing so Cluster stacks. */
    breakdownExcludeCluster = true,
}) {
    var key = [];
    if (periodicity === 'weekly') {
        var temp = Object.keys(data[0]);
        temp.shift();
        for (var i = 0; i < temp.length; i++) {
            key.push(temp[i].split(' ')[0]);
        }
    } else {
        key = Object.keys(data[0]);
        key.shift();
    }

    const countMode = isCountMode(groupByName);
    const yUnitLabel = countMode ? 'count' : 'cost';
    const seriesName = countMode ? 'Count' : 'Cost';

    if (alwaysColumnTotal) {
        const columnData = alignTotalsWithCategories(totalCost, key.length);
        const columnSeries = [{ name: seriesName, data: columnData }];

        var breakdownSeries = [];
        for (var bi = 0; bi < data.length; bi++) {
            var bdict = {};
            const rawName = data[bi][groupBy];
            bdict['name'] =
                rawName != null && rawName !== '' ? String(rawName) : '—';
            var bvalues = Object.values(data[bi]);
            bvalues.shift();
            var btemp = [];
            for (var bj = 0; bj < bvalues.length; bj++) {
                const bn = Number(bvalues[bj]);
                btemp.push(Number.isFinite(bn) ? bn : 0);
            }
            bdict['data'] = alignTotalsWithCategories(btemp, key.length);
            breakdownSeries.push(bdict);
        }

        const clusterBlocksBreakdown =
            breakdownExcludeCluster && groupBy === 'cluster';
        const periodicityAllowsBreakdown =
            enableBreakdownForDaily ||
            periodicity === 'weekly' ||
            periodicity === 'monthly';
        const useBreakdown =
            showServiceBreakdown &&
            periodicityAllowsBreakdown &&
            !clusterBlocksBreakdown;

        const yAxisFormat = {
            title: {
                text: `Total ${yUnitLabel} on ${periodicity} basis`,
            },
            min: 0,
            labels: {
                formatter: function (val) {
                    return countMode
                        ? val.toFixed(0)
                        : val.toFixed(2) + '  $';
                },
            },
        };

        const mergedOptions = {
            chart: {
                type: 'bar',
                height: 'auto',
                toolbar: { show: true },
                animations: { enabled: false },
                stacked: useBreakdown,
            },
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '55%',
                    borderRadius: useBreakdown ? 0 : 2,
                    dataLabels: {
                        enabled: false,
                        total: { enabled: false },
                    },
                },
            },
            dataLabels: { enabled: false },
            title: {
                text: useBreakdown
                    ? `Total ${yUnitLabel} distributed by ${UppercaseFirstLetter(
                          groupBy
                      )} vs Time`
                    : countMode
                    ? 'Total count vs Time'
                    : 'Total Cost vs Time',
            },
            xaxis: {
                categories: key,
            },
            yaxis: yAxisFormat,
            ...(useBreakdown
                ? {
                      stroke: { width: 1, colors: ['#fff'] },
                      legend: {
                          show: true,
                          position: 'top',
                          horizontalAlign: 'left',
                          offsetX: 40,
                      },
                  }
                : {
                      legend: { show: false },
                  }),
        };

        return (
            <div>
                <ReactApexChart
                    key={useBreakdown ? 'org-wise-stacked' : 'org-wise-total'}
                    options={mergedOptions}
                    series={useBreakdown ? breakdownSeries : columnSeries}
                    type="bar"
                    width={1300}
                />
            </div>
        );
    }

    var series = [];
    var options = {};
    if (periodicity === 'daily' || groupBy === 'cluster') {
        series = [
            {
                name: seriesName,
                data: alignTotalsWithCategories(totalCost, key.length),
            },
        ];
        options = {
            chart: {
                type: 'bar',
                height: 'auto',
                toolbar: { show: true },
                animations: { enabled: false },
            },
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '55%',
                    borderRadius: 2,
                },
            },
            dataLabels: { enabled: false },
            title: {
                text: countMode ? 'Total count vs Time' : 'Total Cost vs Time',
            },
            xaxis: {
                categories: key,
            },
            yaxis: {
                title: {
                    text: `Total ${yUnitLabel} on ${periodicity} basis`,
                },
                min: 0,
                labels: {
                    formatter: function (val) {
                        return countMode
                            ? val.toFixed(0)
                            : val.toFixed(2) + '  $';
                    },
                },
            },
            legend: { show: false },
        };
    } else {
        for (var i = 0; i < data.length; i++) {
            var dict = {};
            dict['name'] = data[i][groupBy];
            var values = Object.values(data[i]);
            values.shift();
            var temp2 = [];
            for (var j = 0; j < values.length; j++) {
                temp2.push(values[j].toFixed(2));
            }
            dict['data'] = temp2;
            series.push(dict);
        }

        options = {
            chart: {
                type: 'bar',
                height: 'auto',
                stacked: true,
            },
            plotOptions: {
                bar: {
                    horizontal: true,
                    dataLabels: {
                        enabled: false,
                        total: { enabled: false },
                    },
                },
            },
            stroke: {
                width: 1,
                colors: ['#fff'],
            },
            title: {
                text: `Total ${yUnitLabel} distributed by ${UppercaseFirstLetter(
                    groupBy
                )} vs Time`,
            },
            xaxis: {
                categories: key,
                labels: {
                    formatter: function (val) {
                        return val.toFixed(2) + '  $';
                    },
                },
            },
            yaxis: {
                title: {
                    text: `Total ${yUnitLabel} on ${periodicity} basis`,
                },
                min: 0,
            },
            fill: {
                opacity: 4,
            },
            legend: {
                position: 'top',
                horizontalAlign: 'left',
                offsetX: 40,
            },
        };
    }

    return (
        <div>
            {periodicity === 'daily' || groupBy === 'cluster' ? (
                <ReactApexChart
                    options={options}
                    series={series}
                    type="bar"
                    width={1300}
                />
            ) : (
                <ReactApexChart
                    options={options}
                    series={series}
                    type="bar"
                    height="auto"
                    width={1300}
                />
            )}
        </div>
    );
}
